import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Sparkles, Loader2, Download, FileSpreadsheet, ScanText, Copy, AlertTriangle, Wand2, GitMerge, ShieldAlert,
} from "lucide-react";
import { FileDropzone } from "@/components/ui/FileDropzone";
import { useClaudeAI } from "@/hooks/useClaudeAI";
import { summarizeForAI, downloadCsv, downloadXlsx, type ParsedFile } from "@/lib/file-parsing";
import { toast } from "sonner";

const INDUSTRIES = [
  { value: "general", label: "General / Universal" },
  { value: "finance", label: "Finance" },
  { value: "ecommerce", label: "E-commerce" },
  { value: "logistics", label: "Logistics" },
  { value: "realestate", label: "Real Estate" },
  { value: "technology", label: "Technology" },
];

const FEATURES = [
  { key: "data_cleaning_intelligent", label: "Intelligent Cleaning", icon: Sparkles },
  { key: "format_detection", label: "Format Detection", icon: ScanText },
  { key: "duplicate_detection", label: "Duplicate Detection", icon: Copy },
  { key: "missing_values", label: "Missing Values", icon: AlertTriangle },
  { key: "data_enrichment", label: "Data Enrichment", icon: Wand2 },
  { key: "anomaly_detection", label: "Anomaly Detection", icon: ShieldAlert },
] as const;

type FeatureKey = typeof FEATURES[number]["key"];

export const AdvancedAIDataCleaner = () => {
  const [industry, setIndustry] = useState("general");
  const [parsed, setParsed] = useState<ParsedFile | null>(null);
  const [results, setResults] = useState<Partial<Record<FeatureKey, any>>>({});
  const [active, setActive] = useState<FeatureKey>("data_cleaning_intelligent");
  const { call, isLoading } = useClaudeAI();
  const [runningKey, setRunningKey] = useState<FeatureKey | null>(null);

  const runFeature = async (feature: FeatureKey) => {
    if (!parsed) { toast.error("Upload a dataset first"); return; }
    setRunningKey(feature);
    const res = await call({
      feature,
      dataset: summarizeForAI(parsed),
      options: { industry, fileName: parsed.fileName },
    });
    setRunningKey(null);
    if (res.success) {
      setResults((prev) => ({ ...prev, [feature]: res.json ?? res.response }));
      setActive(feature);
    }
  };

  const exportCleanedDataset = (format: "csv" | "xlsx") => {
    if (!parsed?.rows?.length) { toast.error("No dataset loaded"); return; }
    const name = `cleaned-${parsed.fileName.replace(/\.[^.]+$/, "")}`;
    if (format === "csv") downloadCsv(name, parsed.rows, parsed.columns);
    else downloadXlsx(name, parsed.rows);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Sparkles className="h-5 w-5 text-primary" /> Advanced AI Data Cleaning
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <label className="text-sm font-medium">Industry</label>
            <Select value={industry} onValueChange={setIndustry}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {INDUSTRIES.map((i) => <SelectItem key={i.value} value={i.value}>{i.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <FileDropzone onParsed={(p) => { setParsed(p); setResults({}); }} label="Upload dataset" />
        </div>

        <div className="grid gap-2 md:grid-cols-3">
          {FEATURES.map((f) => {
            const Icon = f.icon;
            const isRunning = runningKey === f.key;
            const hasResult = !!results[f.key];
            return (
              <Button
                key={f.key}
                variant={hasResult ? "default" : "outline"}
                disabled={!parsed || isLoading}
                onClick={() => runFeature(f.key)}
                className="justify-start"
              >
                {isRunning ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Icon className="mr-2 h-4 w-4" />}
                {f.label}
              </Button>
            );
          })}
        </div>

        {Object.keys(results).length > 0 && (
          <div className="space-y-3 pt-2">
            <Tabs value={active} onValueChange={(v) => setActive(v as FeatureKey)}>
              <TabsList className="flex-wrap h-auto">
                {FEATURES.filter((f) => results[f.key]).map((f) => (
                  <TabsTrigger key={f.key} value={f.key}>{f.label}</TabsTrigger>
                ))}
              </TabsList>
              {FEATURES.map((f) => (
                <TabsContent key={f.key} value={f.key}>
                  <ResultViewer feature={f.key} data={results[f.key]} />
                </TabsContent>
              ))}
            </Tabs>

            <div className="flex flex-wrap gap-2 pt-2 border-t">
              <Button size="sm" variant="outline" onClick={() => exportCleanedDataset("csv")}>
                <Download className="h-3 w-3 mr-1" /> Download CSV
              </Button>
              <Button size="sm" variant="outline" onClick={() => exportCleanedDataset("xlsx")}>
                <FileSpreadsheet className="h-3 w-3 mr-1" /> Download Excel
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const ResultViewer = ({ feature, data }: { feature: string; data: any }) => {
  if (!data) return null;
  if (typeof data === "string") {
    return <pre className="text-xs whitespace-pre-wrap bg-muted p-3 rounded">{data}</pre>;
  }
  return (
    <div className="space-y-3">
      {data.summary && (
        <div className="rounded-lg border bg-muted/40 p-3">
          <p className="text-sm font-medium">Summary</p>
          <p className="text-sm">{data.summary}</p>
        </div>
      )}
      {data.issues && (
        <Section title={`Issues Found (${data.issues.length})`}>
          <ul className="text-sm space-y-1">
            {data.issues.map((i: any, k: number) => (
              <li key={k}>
                <Badge variant="outline" className="mr-1">{i.type}</Badge>
                <span className="font-medium">{i.column ?? "—"}</span>: {i.fix} <span className="text-muted-foreground">({i.rows_affected ?? 0} rows)</span>
              </li>
            ))}
          </ul>
        </Section>
      )}
      {data.actions_taken && (
        <Section title="Actions Taken">
          <ul className="text-sm space-y-1">
            {data.actions_taken.map((a: any, k: number) => (
              <li key={k}>✓ {a.action} <span className="text-muted-foreground">({a.rows_affected ?? 0} rows)</span></li>
            ))}
          </ul>
        </Section>
      )}
      {data.columns && Array.isArray(data.columns) && (
        <Section title="Columns">
          <ul className="text-sm space-y-1">
            {data.columns.map((c: any, k: number) => (
              <li key={k}><b>{c.name}</b>: {c.suggested_strategy ?? c.standard ?? "—"} {c.reason ? `— ${c.reason}` : ""}</li>
            ))}
          </ul>
        </Section>
      )}
      {data.exact_duplicates && (
        <Section title={`Exact Duplicates (${data.exact_duplicates.length})`}>
          <pre className="text-xs bg-muted p-2 rounded overflow-auto max-h-40">{JSON.stringify(data.exact_duplicates, null, 2)}</pre>
        </Section>
      )}
      {data.fuzzy_duplicates && (
        <Section title={`Fuzzy Duplicates (${data.fuzzy_duplicates.length})`}>
          <pre className="text-xs bg-muted p-2 rounded overflow-auto max-h-40">{JSON.stringify(data.fuzzy_duplicates, null, 2)}</pre>
        </Section>
      )}
      {data.derived_fields && (
        <Section title="Suggested Derived Fields">
          <ul className="text-sm space-y-1">
            {data.derived_fields.map((d: any, k: number) => (
              <li key={k}><b>{d.name}</b> = <code className="text-xs">{d.formula}</code> — {d.rationale}</li>
            ))}
          </ul>
        </Section>
      )}
      {data.anomalies && (
        <Section title={`Anomalies (${data.anomalies.length})`}>
          <ul className="text-sm space-y-1 max-h-48 overflow-auto">
            {data.anomalies.map((a: any, k: number) => (
              <li key={k}><b>{a.column}</b> row {a.row_index}: {String(a.value)} — {a.explanation} <Badge variant="secondary">{a.recommendation}</Badge></li>
            ))}
          </ul>
        </Section>
      )}
    </div>
  );
};

const Section = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <div className="rounded-lg border p-3">
    <h5 className="font-semibold text-sm mb-2">{title}</h5>
    {children}
  </div>
);
